import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Mascot } from "@/components/styleguide/Mascot";
import { api } from "./api";
import { TermsSheet } from "./TermsSheet";
import { MaintenancePage } from "./MaintenancePage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await api("/auth/me"));
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) {
      /* no-op */
    }
    localStorage.removeItem("bloom-vocab-mode");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function FullLoader({ label = "Loading Bloom…" }) {
  return (
    <div
      className="flex min-h-[100svh] flex-col items-center justify-center gap-3 bg-background"
      data-testid="vocab-loader"
    >
      <Mascot size={72} />
      <p className="text-sm font-bold text-muted-foreground">{label}</p>
    </div>
  );
}

// ─── RequireAuth ─────────────────────────────────────────────────────────────
// Wraps any protected route.  Guards run in order:
//   1. Not loaded yet           → full-screen loader
//   2. No session               → redirect to /vocab (login page)
//   3. Haven't accepted terms   → show TermsSheet overlay (PRIMARY gate)
//      TermsSheet calls POST /users/accept-terms, then refresh()
//      which re-fetches /auth/me and sets accepted_terms_at → guard clears
//   4. Maintenance mode on      → show MaintenancePage (admins always bypass)
//      Controlled by MAINTENANCE_MODE + MAINTENANCE_ALLOWED_EMAILS env vars.
//      Fields arrive on the user object from /auth/me and /auth/google — no
//      extra API call needed.
//   5. Admin-only route + not admin → friendly error screen
export function RequireAuth({ children, admin = false }) {
  const { user, loading, refresh } = useAuth();

  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/vocab" replace />;

  // Terms & Privacy gate — catches new sign-ups AND returning users who
  // pre-date the terms feature (accepted_terms_at will be null for them).
  if (!user.accepted_terms_at) {
    return <TermsSheet onAccepted={refresh} />;
  }

  // Maintenance gate — admins always see the real app regardless of the
  // allow-list, so they can check everything is working before lifting maintenance.
  if (user.maintenance_active && !user.maintenance_allowed && user.role !== "admin") {
    return <MaintenancePage />;
  }

  if (admin && user.role !== "admin") {
    return (
      <div
        className="flex min-h-[100svh] flex-col items-center justify-center gap-3 bg-background px-6 text-center"
        data-testid="admin-denied"
      >
        <Mascot size={72} mood="happy" animated={false} />
        <h1 className="font-display text-2xl font-black text-foreground">Admins only</h1>
        <p className="max-w-sm text-sm font-semibold text-muted-foreground">
          This area manages Bloom's vocabulary library. Your account doesn't have admin access.
        </p>
        <a href="/vocab/children" className="mt-2 font-display font-extrabold text-primary-strong underline">
          Back to Bloom
        </a>
      </div>
    );
  }

  return children;
}
